/**
 * Offline Storage for Lite Invoice Mode
 * Uses IndexedDB for reliable offline invoice storage
 * Queues invoices when offline, syncs when back online
 */

const DB_NAME = 'oproadservice_offline';
const DB_VERSION = 1;
const STORE_NAME = 'pending_invoices';

let db = null;

/**
 * Initialize IndexedDB
 */
export const initOfflineDB = () => {
  return new Promise((resolve, reject) => {
    if (db) {
      resolve(db);
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error('Failed to open offline database');
      reject(request.error);
    };

    request.onsuccess = () => {
      db = request.result;
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = event.target.result;
      
      if (!database.objectStoreNames.contains(STORE_NAME)) {
        const store = database.createObjectStore(STORE_NAME, { 
          keyPath: 'localId', 
          autoIncrement: true 
        });
        store.createIndex('createdAt', 'createdAt', { unique: false });
        store.createIndex('synced', 'synced', { unique: false });
      }
    };
  });
};

/**
 * Save invoice to offline storage
 */
export const saveOfflineInvoice = async (invoiceData) => {
  await initOfflineDB();
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    
    const offlineInvoice = {
      ...invoiceData,
      createdAt: new Date().toISOString(),
      synced: false,
      localInvoiceNumber: `OFFLINE-${Date.now()}`
    };
    
    const request = store.add(offlineInvoice);
    
    request.onsuccess = () => {
      resolve({
        ...offlineInvoice,
        localId: request.result,
        invoice_number: offlineInvoice.localInvoiceNumber
      });
    };
    
    request.onerror = () => {
      reject(request.error);
    };
  });
};

/**
 * Get all pending (unsynced) invoices
 */
export const getPendingInvoices = async () => {
  await initOfflineDB();
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    // Get all and filter for unsynced
    const request = store.getAll();
    
    request.onsuccess = () => {
      const pending = (request.result || []).filter(inv => !inv.synced);
      resolve(pending);
    };
    
    request.onerror = () => {
      reject(request.error);
    };
  });
};

/**
 * Mark invoice as synced and update with server data
 */
export const markInvoiceSynced = async (localId, serverInvoice) => {
  await initOfflineDB();
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(localId);
    
    request.onsuccess = () => {
      const invoice = request.result;
      if (invoice) {
        invoice.synced = true;
        invoice.serverId = serverInvoice.id;
        invoice.serverInvoiceNumber = serverInvoice.invoice_number;
        invoice.syncedAt = new Date().toISOString();
        
        store.put(invoice);
        resolve(invoice);
      } else {
        reject(new Error('Invoice not found'));
      }
    };
    
    request.onerror = () => {
      reject(request.error);
    };
  });
};

/**
 * Delete synced invoices older than 7 days
 */
export const cleanupSyncedInvoices = async () => {
  await initOfflineDB();
  
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.openCursor();
    let deleted = 0;
    
    request.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        const invoice = cursor.value;
        if (invoice.synced && new Date(invoice.syncedAt) < sevenDaysAgo) {
          cursor.delete();
          deleted++;
        }
        cursor.continue();
      } else {
        resolve(deleted);
      }
    };
    
    request.onerror = () => {
      reject(request.error);
    };
  });
};

/**
 * Get count of pending invoices
 */
export const getPendingCount = async () => {
  await initOfflineDB();
  
  return new Promise((resolve) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.getAll();
    
    request.onsuccess = () => {
      const pending = (request.result || []).filter(inv => !inv.synced);
      resolve(pending.length);
    };
    
    request.onerror = () => {
      resolve(0); // Return 0 on error instead of rejecting
    };
  });
};

/**
 * Check if browser is online
 */
export const isOnline = () => navigator.onLine;

/**
 * Listen for online/offline events
 */
export const addConnectivityListener = (callback) => {
  const handleOnline = () => callback(true);
  const handleOffline = () => callback(false);
  
  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);
  
  // Return cleanup function
  return () => {
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
  };
};
